import * as React from 'react';

import {
    EmptyBox,
    HeaderListSkeleton,
    LazyBreadcrumb,
    ListSkeleton,
    SkeletonLoader,
    useFullLayout,
} from '@europrocurement/flexy-components';
import {
    FactureOpenSearch,
    FactureOpenSearchSelectorMain,
    FACTURES_OPEN_SEARCH_SLICE_NAME,
    useFacturationService,
} from '@europrocurement/l2d-domain';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useDispatch, useSelector } from 'react-redux';
import {
    FacturesTable,
    PossibleTypes,
    useBilling,
    useDocumentPreviewContext,
    useSearchBar,
    usePanierPay,
    useFactureFilters,
    useFactureColumns,
} from '@europrocurement/l2d-modules';
import { AnyAction } from '@reduxjs/toolkit';
import { Box, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useInitialLoadingState, usePrescriber } from '@europrocurement/l2d-hooks';
import { FactureStatus } from '@europrocurement/l2d-modules/src/modules/Factures/hooks/useFactureFilters';
import { RowSelectionAction, PreFilterDatatableList } from '@europrocurement/flexy-datatable';
import CustomActionsColumn from './CustomActionsColumn';

const getInvoiceLabel = (item: FactureOpenSearch): string => {
    if (item.type === 'AVOIR') return `Avoir ${item.factureNumero}.pdf`;
    return `Facture ${item.factureNumero}.pdf`;
};

const ListeFactures: React.FunctionComponent = function () {
    const dispatch = useDispatch();
    const { prescriberId } = usePrescriber();
    const prescriberIdRef = React.useRef<number | undefined>();
    const datasource: DataSource<FactureOpenSearch> = useSelector(FactureOpenSearchSelectorMain);
    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();
    const { setProfileHeaderVisible, setPageTitle, clearPageTitle } = useFullLayout();

    const { enqueueSnackbar } = useSnackbar();
    const ddmIdFilter = 3;
    const { downloadInvoice } = useBilling({});

    const { previewLoading, setPreviewLoading, setDocumentPreview, documentPreview } =
        useDocumentPreviewContext();

    const { columnsFront } = useFactureColumns();
    const { factureFiltersFront } = useFactureFilters({ prescriberIdRef, ddmIdFilter });
    const { navigateFacturePay } = usePanierPay();

    const customActionsColumn = React.useCallback(
        (item: FactureOpenSearch) => <CustomActionsColumn item={item} />,
        [],
    );

    const columns = React.useMemo(() => {
        const cols = [...columnsFront];
        cols.push({
            label: 'Actions',
            displayLabel: false,
            render: customActionsColumn,
            isDisplayed: true,
            sortable: false,
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
        });
        return cols;
    }, [columnsFront, customActionsColumn]);

    const rowActions: RowSelectionAction[] = [
        {
            icon: 'fa-credit-card',
            label: 'Payer ces factures',
            onClick: (items: Array<FactureOpenSearch>) => {
                if (!items || items.length === 0) return;

                const clientIds = items.map((item) => item.clientId);
                const uniqueClientIds = Array.from(new Set(clientIds));
                if (uniqueClientIds.length > 1) {
                    enqueueSnackbar(
                        'Erreur : les factures sélectionnées doivent être facturées au même client.',
                        { variant: 'error' },
                    );
                    return;
                }

                const ids: string[] = items
                    .map((item) => item.id)
                    .filter((id): boolean => id !== null && id !== undefined)
                    .map((id) => String(id));
                navigateFacturePay(ids);
            },
        },
    ];

    const preFilters: PreFilterDatatableList = {
        label: 'Filtrer par statuts',
        items: [
            {
                label: 'Tout',
                filters: [],
            },
            {
                label: 'A régler',
                filters: [{ field: 'status', value: FactureStatus.TO_PAY }],
            },
            {
                label: 'En retard',
                filters: [{ field: 'status', value: FactureStatus.LATE }],
            },
            {
                label: 'Réglée',
                filters: [{ field: 'status', value: FactureStatus.PAYED }],
            },
        ],
    };
    const { getFactureOpenSearch } = useFacturationService();

    React.useEffect(() => {
        if (!documentPreview) {
            setProfileHeaderVisible(true);
            clearPageTitle();
            return undefined;
        }
        setProfileHeaderVisible(false);
        setPageTitle(documentPreview.fileName);
        return () => {
            setProfileHeaderVisible(true);
            clearPageTitle();
        };
    }, [documentPreview, clearPageTitle, setPageTitle, setProfileHeaderVisible]);

    const handleRowClick = async (item: FactureOpenSearch) => {
        if (previewLoading) return;

        if (item.id) {
            setPreviewLoading(true);
            try {
                const blob = await downloadInvoice(item.id as number);
                setDocumentPreview({
                    fileName: getInvoiceLabel(item),
                    fileType: 'pdf',
                    content: blob as unknown as Blob,
                });
            } catch (err) {
                console.error(err);
                enqueueSnackbar(`Une erreur est survenue lors de la récupération de la facture`, {
                    variant: 'error',
                });
            }
            setPreviewLoading(false);
        }
    };

    const breadcrumbItems = [
        {
            link: '/accueil',
            icon: {
                icon: ['fasl', 'house'] as IconProp,
                sx: { display: 'flex', alignItems: 'center' },
            },
        },
        {
            link: '/factures',
            path: 'Mes factures',
        },
        'Liste de mes factures',
    ];

    // Activation du flag de recherche de factures de formalité
    React.useEffect(() => {
        setSearchBarType(PossibleTypes.FormalityInvoice);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    React.useEffect(() => {
        if (prescriberId && prescriberId !== prescriberIdRef.current) {
            dispatch({
                type: `${FACTURES_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'ddmId',
                    value: ddmIdFilter,
                },
            });
            dispatch({
                type: `${FACTURES_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'prescriberId',
                    value: prescriberId,
                },
            });
            dispatch({
                type: `${FACTURES_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: {
                    key: 'enPreparation',
                    value: false,
                },
            });
            prescriberIdRef.current = prescriberId;
        }
    }, [prescriberId, dispatch]);

    // reFetch factures api datas
    React.useEffect(() => {
        if (prescriberIdRef.current !== undefined) {
            dispatch(getFactureOpenSearch({}) as unknown as AnyAction);
        }
    }, [prescriberIdRef, dispatch, getFactureOpenSearch]);

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des factures."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton columns={6} />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <Box sx={{ position: 'relative' }}>
                <LazyBreadcrumb
                    path={breadcrumbItems}
                    separator=" / "
                    sx={{ marginBottom: '1.5em' }}
                />
                {previewLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                {prescriberIdRef.current && (
                    <FacturesTable
                        datasource={datasource}
                        preFilters={preFilters}
                        columns={columns}
                        filters={factureFiltersFront}
                        fetchData={getFactureOpenSearch}
                        rowActions={rowActions}
                        onRowClick={handleRowClick}
                    />
                )}
            </Box>
        </SkeletonLoader>
    );
};

export default ListeFactures;
